import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { disconnectSocket } from '../hooks/useSocket';
import { usePush } from '../hooks/usePush';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    disconnectSocket();
    logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';
  const { permission, subscribed, loading: pushLoading, subscribe, unsubscribe } = usePush();

  const handlePushToggle = () => {
    if (subscribed) unsubscribe();
    else subscribe();
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Top navbar */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50" style={{paddingTop: 'env(safe-area-inset-top)'}}>
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                </svg>
              </div>
              <span className="font-bold text-white text-sm">VZboilers Мониторинг</span>
            </div>
            {/* Desktop nav */}
            <nav className="hidden sm:flex items-center gap-1">
              {isAdmin ? (
                <>
                  <NavLink to="/admin" end className={({ isActive }) =>
                    `px-3 py-1.5 rounded-lg text-sm transition-colors ${isActive ? 'bg-blue-600/20 text-blue-400' : 'text-slate-400 hover:text-white'}`
                  }>Котельные</NavLink>
                  <NavLink to="/admin/clients" className={({ isActive }) =>
                    `px-3 py-1.5 rounded-lg text-sm transition-colors ${isActive ? 'bg-blue-600/20 text-blue-400' : 'text-slate-400 hover:text-white'}`
                  }>Клиенты</NavLink>
                </>
              ) : (
                <NavLink to="/dashboard" className={({ isActive }) =>
                  `px-3 py-1.5 rounded-lg text-sm transition-colors ${isActive ? 'bg-blue-600/20 text-blue-400' : 'text-slate-400 hover:text-white'}`
                }>Мои котельные</NavLink>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-slate-400 text-sm hidden sm:block">{user?.name}</span>
            {isAdmin && (
              <span className="bg-blue-600/20 text-blue-400 text-xs px-2 py-0.5 rounded-full border border-blue-600/30">
                Админ
              </span>
            )}
            {permission === 'unsupported' ? (
              <span className="text-xs text-slate-500 hidden sm:block">Уведомления не поддерживаются</span>
            ) : permission === 'denied' ? null : (
              <button
                onClick={handlePushToggle}
                disabled={pushLoading}
                title={subscribed ? 'Отключить уведомления' : 'Включить уведомления'}
                className={`p-1 transition-colors ${subscribed ? 'text-blue-400 hover:text-blue-300' : 'text-slate-400 hover:text-white'}`}
              >
                {pushLoading ? (
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                ) : subscribed ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                )}
              </button>
            )}
            <button
              onClick={handleLogout}
              className="text-slate-400 hover:text-white transition-colors p-1"
              title="Выйти"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-6">
        {children}
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="bg-slate-800 border-t border-slate-700 sticky bottom-0 sm:hidden">
        <div className="flex">
          {isAdmin ? (
            <>
              <NavLink to="/admin" end className={({ isActive }) =>
                `flex-1 flex flex-col items-center py-3 text-xs gap-1 transition-colors ${isActive ? 'text-blue-400' : 'text-slate-400'}`
              }>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Котельные
              </NavLink>
              <NavLink to="/admin/clients" className={({ isActive }) =>
                `flex-1 flex flex-col items-center py-3 text-xs gap-1 transition-colors ${isActive ? 'text-blue-400' : 'text-slate-400'}`
              }>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Клиенты
              </NavLink>
            </>
          ) : (
            <NavLink to="/dashboard" className={({ isActive }) =>
              `flex-1 flex flex-col items-center py-3 text-xs gap-1 transition-colors ${isActive ? 'text-blue-400' : 'text-slate-400'}`
            }>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Мои котельные
            </NavLink>
          )}
        </div>
      </nav>
    </div>
  );
}
